export const TOKENS = {
  primary: "var(--talenta-primary)",
  primaryFg: "var(--talenta-primary-foreground)",
  accent: "var(--talenta-accent)",
  accentFg: "var(--talenta-accent-foreground)",
  depth: "var(--talenta-depth)",
  depthElevated: "var(--talenta-depth-elevated)",
  depthFg: "var(--talenta-depth-foreground)",
  surface: "var(--talenta-surface)",
  surfaceElevated: "var(--talenta-surface-elevated)",
  text: "var(--talenta-text)",
  textMuted: "var(--talenta-text-muted)",
  border: "var(--talenta-border)",
  borderStrong: "var(--talenta-border-strong)",
  shadow: "var(--talenta-shadow)",
  shadowStrong: "var(--talenta-shadow-strong)",
  shadowPrimary: "var(--talenta-shadow-primary)",
  radius: "var(--talenta-radius)",
  radiusSm: "var(--talenta-radius-sm)",
} as const;

/**
 * BRAND_PALETTE — slot-based anchors for Talenta's design tokens.
 *
 * Hex literals here MUST match the values declared in `tokens.css` for the
 * corresponding CSS custom property. BUG-041 v2 enforces this lockstep via two
 * gates: the vitest `brand-palette-warmth.test.ts` (parses tokens.css and
 * asserts each --talenta-* value is in the right slot) and `affordance-check.mjs`
 * (CI-level static check with the same assertion).
 *
 * Slots:
 *   warm    — primary CTA-eligible warm anchors. --talenta-primary and
 *             --talenta-accent must each resolve to one of these.
 *   depth   — dark band hexes (login backdrop, AppShell header gradient).
 *             --talenta-depth and --talenta-depth-elevated must each resolve
 *             to one of these. Depth values must NEVER appear in `warm` and
 *             vice versa — that prevents the "warm CTA used as backdrop" or
 *             "depth color used as button" regression.
 *   neutral — surface / text / border greys. Not currently asserted (informational).
 *
 * Adding a new warm anchor or depth color: add it here AND mirror to the
 * WARM_ANCHORS / DEPTH_ANCHORS Sets in `scripts/affordance-check.mjs`.
 */
export const BRAND_PALETTE = {
  warm: {
    terracotta: "#c75634",
    amber: "#d97706",
    cacaoBrown: "#7c4a2e",
  },
  depth: {
    charcoal: "#1f2937",
    charcoalElevated: "#2b3645",
  },
  neutral: {
    cream: "#fdfcf9",
    pureWhite: "#ffffff",
    inkText: "#1f2937",
    mutedText: "#6b7280",
    borderSoft: "#e5e7eb",
    borderStrong: "#d1d5db",
  },
} as const;

export type WarmAnchor = keyof typeof BRAND_PALETTE.warm;
export type DepthAnchor = keyof typeof BRAND_PALETTE.depth;

/**
 * Phase 2 — domain chip palette (7 Stefan holdings).
 *
 * Sibling to BRAND_PALETTE but used for SEMANTIC domain identification on
 * position tiles and chips. NOT subject to brand-palette-warmth.test.ts —
 * domain chips have a named-exception test in domain-chip-warmth-exception.test.ts
 * (3 cool-tones whitelisted: Short-term rentals sea-blue, Supermarket teal,
 * Ultra Luxury Villas violet).
 *
 * Domain enum update 2026-05-21: removed Investment Research (deprecated),
 * renamed S Luxury → Ultra Luxury Villas (matches the new $100K/wk villas
 * concept positioning), added Short-term rentals (Villa Manager domain).
 * Color choices: Ultra Luxury Villas keeps the existing violet (luxury feel
 * preserved); Short-term rentals takes a new sea-blue (#1d4ed8) for the
 * coastal/vacation rental semantic.
 *
 * Source: .planning/phases/02-.../02-UI-SPEC.md §Color (Domain Chip Colors table)
 * + workspace rule 11 product alignment.
 */
export const DOMAIN_PALETTE = {
  finmark:              { bg: "#92400e", text: "#ffffff" },
  ultra_luxury_villas:  { bg: "#5b21b6", text: "#ffffff" },
  construction:         { bg: "#b45309", text: "#ffffff" },
  chocolate:            { bg: "#7c4a2e", text: "#ffffff" },
  agriculture:          { bg: "#3f6212", text: "#ffffff" },
  supermarket:          { bg: "#0e7490", text: "#ffffff" },
  short_term_rentals:   { bg: "#1d4ed8", text: "#ffffff" },
} as const;

export type DomainPaletteKey = keyof typeof DOMAIN_PALETTE;
